import { Injectable, computed, signal } from '@angular/core';

/**
 * Centralized store for auth tokens extracted from URL query parameters.
 * Tokens are persisted in localStorage so they survive Angular Router
 * navigations without requiring the JWT to remain in the URL.
 */
@Injectable({ providedIn: 'root' })
export class TokenService {
  readonly jwtToken = signal<string | null>(null);
  readonly clientId = signal<string | null>(null);
  readonly providerId = signal<string | null>(null);

  /** Derived role based on which ID is present in the token context. */
  readonly role = computed<'admin' | 'client' | 'provider'>(() => {
    if (this.clientId()) return 'client';
    if (this.providerId()) return 'provider';
    return 'admin';
  });

  constructor() {
    this.extractFromUrl();
  }

  /**
   * Persist tokens from a fresh login directly into signals and localStorage.
   * Called by AuthService after a successful login — replaces the location.href
   * approach so no full page reload is needed.
   */
  setTokens(
    jwtToken: string,
    clientId?: number | null,
    organizationId?: number | null,
  ): void {
    const clientIdStr = clientId ? String(clientId) : null;
    const providerIdStr = organizationId ? String(organizationId) : null;

    localStorage.setItem('jwtToken', jwtToken);
    if (clientIdStr) localStorage.setItem('clientId', clientIdStr);
    else localStorage.removeItem('clientId');
    if (providerIdStr) localStorage.setItem('providerId', providerIdStr);
    else localStorage.removeItem('providerId');

    this.jwtToken.set(jwtToken);
    this.clientId.set(clientIdStr);
    this.providerId.set(providerIdStr);
  }

  /** Clear all token data from storage and reset signals (logout). */
  clearTokens(): void {
    localStorage.removeItem('jwtToken');
    localStorage.removeItem('clientId');
    localStorage.removeItem('providerId');
    this.jwtToken.set(null);
    this.clientId.set(null);
    this.providerId.set(null);
  }

  /**
   * Read tokens from the URL on initial iframe entry (token passed via query params).
   * Falls back to localStorage for subsequent in-app navigations.
   * When jwtToken is present in the URL, ONLY URL params are used — stale
   * localStorage values are never mixed with a new login's token.
   */
  private extractFromUrl(): void {
    const params = new URLSearchParams(globalThis.location.search);
    const isInitialEntry = !!params.get('jwtToken');

    if (isInitialEntry) {
      // Fresh entry from parent iframe — use only URL params, never stale storage
      const jwtToken = params.get('jwtToken')!;
      const clientId = params.get('clientId');
      const providerId = params.get('providerId');

      localStorage.setItem('jwtToken', jwtToken);
      if (clientId) localStorage.setItem('clientId', clientId);
      else localStorage.removeItem('clientId');
      if (providerId) localStorage.setItem('providerId', providerId);
      else localStorage.removeItem('providerId');

      this.jwtToken.set(jwtToken);
      this.clientId.set(clientId);
      this.providerId.set(providerId);
    } else {
      // In-app navigation — restore from localStorage
      this.jwtToken.set(localStorage.getItem('jwtToken'));
      this.clientId.set(localStorage.getItem('clientId'));
      this.providerId.set(localStorage.getItem('providerId'));
    }
  }
}
